import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { Amplify } from 'aws-amplify';
import { Parse } from '../../../lib/parse';
import '@aws-amplify/ui-react/styles.css';

/**
 * SelfieWithLivenessStep - Third step for capturing selfie with AWS Rekognition Face Liveness
 * Uses real liveness detection with colored lights and movement challenges
 */
function SelfieWithLivenessStep({ onCapture, onError, isProcessing }) {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);

  useEffect(() => {
    // Configure Amplify and create liveness session when component mounts
    const initialize = async () => {
      try {
        setLoading(true);

        // Get AWS credentials from backend
        const credentials = await Parse.Cloud.run('getAwsCredentials');

        console.log('AWS credentials received, region:', credentials.region);

        // Configure Amplify with AWS credentials
        Amplify.configure({
          Auth: {
            Cognito: {
              identityPoolId: 'us-east-1:dummy-pool-id', // Not used, we provide credentials directly
              region: credentials.region
            }
          }
        }, {
          Auth: {
            credentialsProvider: {
              getCredentialsAndIdentityId: async () => ({
                credentials: {
                  accessKeyId: credentials.accessKeyId,
                  secretAccessKey: credentials.secretAccessKey
                }
              }),
              clearCredentialsAndIdentityId: () => {}
            }
          }
        });

        setAmplifyConfigured(true);

        // Create liveness session
        const result = await Parse.Cloud.run('createLivenessSession');
        console.log('Liveness session created:', result.sessionId);
        setSessionId(result.sessionId);
      } catch (error) {
        console.error('Failed to initialize liveness:', error);
        if (onError) {
          onError(error);
        }
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [onError]);

  // Keep selfie step pinned to the top of the verification modal
  useEffect(() => {
    if (sessionId && amplifyConfigured && !loading) {
      const scrollTimer = setTimeout(() => {
        const modalContent = document.querySelector('.verification-modal-content.selfie-step');
        if (modalContent) {
          modalContent.scrollTo({
            top: 0,
            behavior: 'auto'
          });
        }
      }, 0);

      return () => clearTimeout(scrollTimer);
    }
  }, [sessionId, amplifyConfigured, loading]);

  const handleAnalysisComplete = async () => {
    try {
      console.log('Liveness analysis complete, getting results...');

      // Get liveness session results
      const results = await Parse.Cloud.run('getLivenessSessionResults', {
        sessionId
      });

      console.log('Liveness results:', results);
      console.log('referenceImage exists:', !!results.referenceImage);
      console.log('referenceImage.base64 exists:', !!results.referenceImage?.base64);

      // Get base64 from backend response
      let imageBase64 = null;
      let imageDataUrl = null;

      if (results.referenceImage?.base64) {
        // Backend already converted to base64
        imageBase64 = results.referenceImage.base64;
        imageDataUrl = `data:image/jpeg;base64,${imageBase64}`;
        
        console.log('base64String length:', imageBase64.length);
      } else {
        console.error('No reference image base64 found in results');
      }

      // Build liveness data object
      const livenessData = {
        base64: imageBase64,
        dataUrl: imageDataUrl,
        name: `liveness-${Date.now()}.jpg`,
        type: 'image/jpeg',
        size: results.referenceImage?.Bytes?.length || (imageBase64 ? imageBase64.length * 0.75 : 0), // Approximate size from base64
        capturedAt: Date.now(),
        sessionId: results.sessionId,
        confidence: results.confidence,
        status: results.status,
        auditImages: results.auditImages
      };

      console.log('Calling onCapture with liveness data:', {
        hasBase64: !!livenessData.base64,
        base64Length: livenessData.base64?.length || 0,
        hasDataUrl: !!livenessData.dataUrl
      });

      if (onCapture) {
        onCapture(livenessData);
      }
    } catch (error) {
      console.error('Failed to get liveness results:', error);
      if (onError) {
        onError(error);
      }
    }
  };

  const handleError = (error) => {
    console.error('Liveness detection error:', error);
    if (onError) {
      onError(error);
    }
  };



  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="amplify-loader mb-4"></div>
        <p className="text-sm text-gray-600">
          {t('verification.initializing-liveness', {
            defaultValue: 'Inicializando detección de vida...'
          })}
        </p>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="amplify-loader mb-4"></div>
        <p className="text-sm text-gray-600">
          {t('verification.processing', {
            defaultValue: 'Verificando tu identidad...'
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="verification-selfie-container">
      {sessionId && amplifyConfigured && (
        <div className="verification-selfie-camera-shell">
          <FaceLivenessDetector
            sessionId={sessionId}
            region={process.env.REACT_APP_AWS_REGION || 'us-east-1'}
            onAnalysisComplete={handleAnalysisComplete}
            onError={handleError}
            displayText={{
            hintCenterFaceText: t('verification.hint-center-face', {
              defaultValue: 'Centra tu rostro'
            }),
            hintFaceDetectedText: t('verification.hint-face-detected', {
              defaultValue: 'Rostro detectado'
            }),
            hintTooManyFacesText: t('verification.hint-too-many-faces', {
              defaultValue: 'Asegúrate de que solo tu rostro esté visible'
            }),
            hintFaceDetectedHeadingText: t('verification.hint-face-detected-heading', {
              defaultValue: 'Verificando...'
            }),
            hintTooCloseText: t('verification.hint-too-close', {
              defaultValue: 'Aléjate un poco'
            }),
            hintTooFarText: t('verification.hint-too-far', {
              defaultValue: 'Acércate un poco'
            }),
            hintConnectingText: t('verification.hint-connecting', {
              defaultValue: 'Conectando...'
            }),
            hintVerifyingText: t('verification.hint-verifying', {
              defaultValue: 'Verificando...'
            }),
            hintIlluminationTooBrightText: t('verification.hint-too-bright', {
              defaultValue: 'Muévete a un área menos iluminada'
            }),
            hintIlluminationTooDarkText: t('verification.hint-too-dark', {
              defaultValue: 'Muévete a un área más iluminada'
            }),
            hintIlluminationNormalText: t('verification.hint-illumination-normal', {
              defaultValue: 'Iluminación correcta'
            }),
            hintHoldFaceForFreshnessText: t('verification.hint-hold-face', {
              defaultValue: 'Mantén tu rostro en posición'
            }),
            photosensitivyWarningHeadingText: t('verification.photosensitivity-heading', {
              defaultValue: 'Advertencia de Fotosensibilidad'
            }),
            photosensitivyWarningBodyText: t('verification.photosensitivity-body', {
              defaultValue: 'Esta verificación muestra luces de colores. Precaución si eres fotosensible.'
            }),
            photosensitivyWarningInfoText: t('verification.photosensitivity-info', {
              defaultValue: 'Precaución si tú o alguien en tu familia tiene epilepsia.'
            }),
            goodFitCaptionText: t('verification.good-fit', {
              defaultValue: 'Buen ajuste'
            }),
            tooFarCaptionText: t('verification.too-far-caption', {
              defaultValue: 'Muy lejos'
            }),
            startScreenBeginCheckText: t('verification.begin-check', {
              defaultValue: 'Iniciar verificación'
            }),
            cancelLivenessCheckText: t('verification.cancel-check', {
              defaultValue: 'Cancelar'
            }),
            recordingIndicatorText: t('verification.recording', {
              defaultValue: 'Grabando'
            })
          }}
          />
        </div>
      )}
    </div>
  );
}

export default SelfieWithLivenessStep;
